import React, { useState, useContext, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import UserAvatar from "../components/UserAvatar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Award,
  Calendar,
  Target,
  Trophy,
  Clock,
  Plus,
  Edit2,
  Trash2,
  Pause,
  Play,
  Check,
  X,
  Smile,
  Users,
  BarChart3,
  Crown,
  TrendingUp,
  PieChart,
  AlertTriangle,
  CheckCircle2,
  StopCircle,
  Upload,
  Image,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import nominationsService from "../services/nominationsService";
import recognitionTypesService from "../services/recognitionTypesService";
import usersService from "../services/usersService";
import useCustomToast from "../hooks/useCustomToast";
import { formatError } from "../utils/errorHandler";
import { useTranslation, useFormat } from "../i18n";

// ── Constants ──

const GRADIENT_PRESETS = [
  { from: "#3b82f6", to: "#06b6d4", key: "blue_cyan" },
  { from: "#22c55e", to: "#10b981", key: "green_emerald" },
  { from: "#eab308", to: "#f97316", key: "yellow_orange" },
  { from: "#a855f7", to: "#ec4899", key: "purple_pink" },
  { from: "#6366f1", to: "#3b82f6", key: "indigo_blue" },
  { from: "#f59e0b", to: "#eab308", key: "amber_yellow" },
  { from: "#ef4444", to: "#f43f5e", key: "red_rose" },
  { from: "#14b8a6", to: "#22c55e", key: "teal_green" },
  { from: "#ec4899", to: "#a855f7", key: "pink_purple" },
  { from: "#06b6d4", to: "#3b82f6", key: "cyan_blue" },
  { from: "#f97316", to: "#ef4444", key: "orange_red" },
  { from: "#84cc16", to: "#22c55e", key: "lime_green" },
];

const COLOR_PALETTE = [
  ["#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c", "#991b1b"],
  ["#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c", "#9a3412"],
  ["#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04", "#a16207", "#854d0e"],
  ["#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d", "#166534"],
  ["#99f6e4", "#5eead4", "#2dd4bf", "#14b8a6", "#0d9488", "#0f766e", "#115e59"],
  ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8", "#1e40af"],
  ["#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#4338ca", "#3730a3"],
  ["#e9d5ff", "#d8b4fe", "#c084fc", "#a855f7", "#9333ea", "#7e22ce", "#6b21a8"],
  ["#fbcfe8", "#f9a8d4", "#f472b6", "#ec4899", "#db2777", "#be185d", "#9d174d"],
  ["#e5e7eb", "#d1d5db", "#9ca3af", "#6b7280", "#4b5563", "#374151", "#1f2937"],
];

const EMOJI_CATEGORIES = {
  people: ["🤝", "💪", "👏", "🙌", "👍", "✋", "🤲", "👑"],
  objects: ["💼", "💡", "🎯", "⭐", "🏆", "🎖️", "💎", "🔑"],
  celebration: ["🎉", "🎊", "✨", "🌟", "🔥", "💥", "⚡", "🎁"],
  hearts: ["❤️", "💜", "💙", "💚", "🧡", "💛", "🤍", "💖"],
  nature: ["🚀", "🌈", "☀️", "🌙", "🦁", "🦅", "🐝", "🌸"],
  sports: ["🥇", "🥈", "🥉", "🎮", "🎨", "🎵", "📚", "💻"],
};

const QUICK_EMOJIS = ["🤝", "💼", "💡", "👑", "🎯", "⭐", "🚀", "💪", "🏆", "🎉", "❤️", "🔥", "✨", "🌟", "💎", "🎖️"];

const CHART_COLORS = ["#8b5cf6", "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#6366f1", "#f97316", "#84cc16"];

const COLOR_MAP = {
  purple: { bg: "from-purple-50 to-purple-100", border: "border-purple-200", text: "text-purple-600", label: "text-purple-700" },
  blue: { bg: "from-blue-50 to-blue-100", border: "border-blue-200", text: "text-blue-600", label: "text-blue-700" },
  green: { bg: "from-green-50 to-green-100", border: "border-green-200", text: "text-green-600", label: "text-green-700" },
  yellow: { bg: "from-yellow-50 to-yellow-100", border: "border-yellow-200", text: "text-yellow-600", label: "text-yellow-700" },
  teal: { bg: "from-teal-50 to-teal-100", border: "border-teal-200", text: "text-teal-600", label: "text-teal-700" },
  pink: { bg: "from-pink-50 to-pink-100", border: "border-pink-200", text: "text-pink-600", label: "text-pink-700" },
};

const TABS = [
  { id: "overview", icon: Award },
  { id: "leaderboard", icon: BarChart3 },
  { id: "winners", icon: Crown },
  { id: "analytics", icon: TrendingUp },
];

// ── Shared helpers ──

const KpiTile = ({ icon: Icon, label, value, color }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.purple;
  return (
    <div className={`p-4 bg-gradient-to-br ${c.bg} rounded-2xl border ${c.border}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${c.text}`} />
        <p className={`text-xs font-medium ${c.label}`}>{label}</p>
      </div>
      <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
    </div>
  );
};

const extractHexColor = (colorString) => {
  if (!colorString) return null;
  if (colorString.startsWith("#")) return colorString;
  const match = colorString.match(/\[(.*?)\]/);
  return match ? match[1] : colorString;
};

const getDaysRemaining = (endDate) => {
  const end = new Date(endDate);
  const now = new Date();
  const diff = end - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const getStatusBadge = (status, t) => {
  const statusMap = {
    active: { label: t("status.active"), className: "bg-green-100 text-green-700" },
    paused: { label: t("status.paused"), className: "bg-amber-100 text-amber-700" },
    draft: { label: t("status.draft"), className: "bg-slate-100 text-slate-700" },
    completed: { label: t("status.completed"), className: "bg-blue-100 text-blue-700" },
    cancelled: { label: t("status.cancelled"), className: "bg-red-100 text-red-700" },
  };
  const statusInfo = statusMap[status] || statusMap.draft;
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.className}`}>
      {statusInfo.label}
    </span>
  );
};

// ── Main Component ──

const CampaignPage = () => {
  const { t } = useTranslation("nominations");
  const fmt = useFormat();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { user: currentUser } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "overview";

  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  const [editingAward, setEditingAward] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickingColor, setPickingColor] = useState(null);
  const [iconTab, setIconTab] = useState("emojis");
  const [showWinnerModal, setShowWinnerModal] = useState(null); // award object or null
  const [winnerAdminNote, setWinnerAdminNote] = useState("");
  const [campaignFormData, setCampaignFormData] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    emoji: "⭐",
    color_from: "#a855f7",
    color_to: "#3b82f6",
    points_value: 0,
    display_order: 0,
    is_active: true,
  });

  // ── Queries ──

  const { data: campaign, isLoading: campaignLoading, error: campaignError } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => nominationsService.getCampaignById(id),
    enabled: !!id,
  });

  const { data: awardsResponse, isLoading: awardsLoading } = useQuery({
    queryKey: ["campaign-awards", id],
    queryFn: () => nominationsService.getCampaignAwards(id, true),
    enabled: !!id,
  });

  const { data: leaderboardResponse } = useQuery({
    queryKey: ["campaign-leaderboard", id],
    queryFn: () => nominationsService.getCampaignLeaderboard(id),
    enabled: !!id && (currentTab === "leaderboard" || currentTab === "winners" || currentTab === "analytics"),
  });

  const { data: usersData } = useQuery({
    queryKey: ["colleagues"],
    queryFn: () => usersService.getColleagues(0, 500),
    enabled: currentTab === "winners" || currentTab === "leaderboard" || currentTab === "analytics",
  });

  const awards = useMemo(() => awardsResponse?.data || [], [awardsResponse]);
  const leaderboard = useMemo(() => leaderboardResponse?.data || [], [leaderboardResponse]);

  const usersMap = useMemo(() => {
    const map = {};
    if (currentUser) {
      map[currentUser.id] = currentUser;
    }
    const list = Array.isArray(usersData) ? usersData : usersData?.data || [];
    list.forEach((u) => { map[u.id] = u; });
    return map;
  }, [usersData, currentUser]);

  // Helper to check if an icon value is a custom icon URL
  const isCustomIconUrl = (icon) => icon && icon.startsWith("/api/v1/recognition-type-icons/");

  // Custom icons query (org-level, shared with recognition types)
  const orgId = currentUser?.organization_id;
  const {
    data: customIcons = [],
    refetch: refetchCustomIcons,
  } = useQuery({
    queryKey: ["recognition-type-custom-icons", orgId],
    queryFn: () => recognitionTypesService.getCustomIcons(orgId),
    enabled: !!orgId,
  });

  // Upload custom icon mutation
  const uploadIconMutation = useMutation({
    mutationFn: (file) => recognitionTypesService.uploadCustomIcon(orgId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["recognition-type-custom-icons", orgId] });
      toast.success(t("campaign.toasts.icon_uploaded"));
      setFormData((prev) => ({ ...prev, emoji: data.url }));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Delete custom icon mutation
  const deleteIconMutation = useMutation({
    mutationFn: (iconId) => recognitionTypesService.deleteCustomIcon(iconId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-type-custom-icons", orgId] });
      toast.success(t("campaign.toasts.icon_deleted"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Handle custom icon file upload
  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/png", "image/x-icon"];
    const validExtensions = [".png", ".ico"];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      toast.error(t("campaign.toasts.icon_invalid_type"));
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error(t("campaign.toasts.icon_too_large"));
      return;
    }
    uploadIconMutation.mutate(file);
    e.target.value = "";
  };

  // ── Mutations ──

  const pauseMutation = useMutation({
    mutationFn: () => nominationsService.pauseCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("campaign.toasts.paused"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const endCampaignMutation = useMutation({
    mutationFn: () => nominationsService.endCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("campaign.toasts.ended"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (isActive) => nominationsService.updateCampaignStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("campaign.toasts.status_updated"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: () => nominationsService.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("campaign.toasts.deleted"));
      navigate("/admin");
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: (data) => nominationsService.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(t("campaign.toasts.updated"));
      setShowEditCampaignModal(false);
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const createAwardMutation = useMutation({
    mutationFn: (data) => nominationsService.createCampaignAward(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-awards", id] });
      toast.success(t("campaign.toasts.award_created"));
      closeAwardModal();
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const updateAwardMutation = useMutation({
    mutationFn: ({ awardId, data }) => nominationsService.updateCampaignAward(id, awardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-awards", id] });
      toast.success(t("campaign.toasts.award_updated"));
      closeAwardModal();
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const deleteAwardMutation = useMutation({
    mutationFn: (awardId) => nominationsService.deleteCampaignAward(id, awardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-awards", id] });
      toast.success(t("campaign.toasts.award_deleted"));
      setDeleteConfirmId(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
      setDeleteConfirmId(null);
    },
  });

  const selectWinnerMutation = useMutation({
    mutationFn: ({ awardId, userId, adminNote }) =>
      nominationsService.selectAwardWinner(id, awardId, userId, adminNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-awards", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-leaderboard", id] });
      toast.success(t("campaign.toasts.winner_selected"));
      setShowWinnerModal(null);
      setWinnerAdminNote("");
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const clearWinnerMutation = useMutation({
    mutationFn: (awardId) => nominationsService.clearAwardWinner(id, awardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-awards", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-leaderboard", id] });
      toast.success(t("campaign.toasts.winner_cleared"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const finalizeMutation = useMutation({
    mutationFn: () => nominationsService.finalizeCampaign(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      queryClient.invalidateQueries({ queryKey: ["campaign-awards", id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      const total = data?.data?.total_points_awarded || 0;
      toast.success(t("campaign.toasts.finalized", { points: fmt.number(total) }));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // ── Handlers ──

  const handleTabChange = (tabId) => setSearchParams({ tab: tabId });

  const openForCreate = () => {
    setEditingAward(null);
    setFormData({ name: "", emoji: "⭐", color_from: "#a855f7", color_to: "#3b82f6", points_value: 0, display_order: awards.length, is_active: true });
    setShowEmojiPicker(false);
    setPickingColor(null);
    setIconTab("emojis");
    setShowAwardModal(true);
  };

  const openForEdit = (award) => {
    setEditingAward(award);
    const emoji = award.emoji || "⭐";
    setFormData({
      name: award.name,
      emoji,
      color_from: extractHexColor(award.color_from) || "#a855f7",
      color_to: extractHexColor(award.color_to) || "#3b82f6",
      points_value: award.points_value || 0,
      display_order: award.display_order || 0,
      is_active: award.is_active !== false,
    });
    setShowEmojiPicker(false);
    setPickingColor(null);
    setIconTab(isCustomIconUrl(emoji) ? "custom" : "emojis");
    setShowAwardModal(true);
  };

  const closeAwardModal = () => {
    setShowAwardModal(false);
    setEditingAward(null);
    setShowEmojiPicker(false);
    setPickingColor(null);
    setIconTab("emojis");
  };

  const openEditCampaignModal = () => {
    if (campaign) {
      setCampaignFormData({
        name: campaign.name || "",
        description: campaign.description || "",
        start_date: campaign.start_date || "",
        end_date: campaign.end_date || "",
      });
      setShowEditCampaignModal(true);
    }
  };

  const handleCampaignSubmit = (e) => {
    e.preventDefault();
    if (!campaignFormData.name.trim()) {
      toast.error(t("edit_campaign.name_required"));
      return;
    }
    updateCampaignMutation.mutate(campaignFormData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t("award_form.name_required"));
      return;
    }
    const payload = {
      name: formData.name.trim(),
      emoji: formData.emoji,
      color_from: formData.color_from,
      color_to: formData.color_to,
      points_value: formData.points_value,
      display_order: formData.display_order,
      is_active: formData.is_active,
    };
    if (editingAward) {
      updateAwardMutation.mutate({ awardId: editingAward.id, data: payload });
    } else {
      createAwardMutation.mutate(payload);
    }
  };

  const handleColorSelect = (color) => {
    if (pickingColor === "from") setFormData({ ...formData, color_from: color });
    else if (pickingColor === "to") setFormData({ ...formData, color_to: color });
    setPickingColor(null);
  };

  const handleGradientPreset = (preset) => {
    setFormData({ ...formData, color_from: preset.from, color_to: preset.to });
    setPickingColor(null);
  };

  const handleEndCampaign = () => {
    if (window.confirm(t("campaign.confirm_end"))) {
      endCampaignMutation.mutate();
    }
  };

  const handleDeleteCampaign = () => {
    if (window.confirm(t("campaign.confirm_delete"))) {
      deleteCampaignMutation.mutate();
    }
  };

  const handleFinalize = () => {
    if (window.confirm(t("campaign.confirm_finalize"))) {
      finalizeMutation.mutate();
    }
  };

  // ── Analytics data ──

  const analyticsData = useMemo(() => {
    const noms = campaign?.nominations || [];
    if (noms.length === 0) return null;

    const uniqueNominators = new Set(noms.map((n) => n.sender_id));
    const uniqueNominees = new Set(noms.map((n) => n.recipient_id));

    // Most nominated user
    const recipientCounts = {};
    noms.forEach((n) => {
      recipientCounts[n.recipient_id] = (recipientCounts[n.recipient_id] || 0) + 1;
    });
    const topRecipientId = Object.entries(recipientCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topRecipient = usersMap[topRecipientId];

    // Nominations per award
    const awardCounts = {};
    noms.forEach((n) => {
      if (n.award_id) {
        const aw = awards.find((a) => a.id === n.award_id);
        const name = aw?.name || t("unknown");
        awardCounts[name] = (awardCounts[name] || 0) + 1;
      }
    });
    const perAwardData = Object.entries(awardCounts).map(([name, count]) => ({ name, count }));

    // Timeline (group by day)
    const byDay = {};
    noms.forEach((n) => {
      const d = n.timestamp ? new Date(n.timestamp) : null;
      const day = d && !Number.isNaN(d.getTime())
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
        : "";
      byDay[day] = (byDay[day] || 0) + 1;
    });
    const timelineData = Object.entries(byDay)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([day, count]) => ({ date: day ? fmt.date(`${day}T00:00:00`) : t("unknown"), count }));

    // Pie chart data
    const pieData = perAwardData.map((d, i) => ({ ...d, fill: CHART_COLORS[i % CHART_COLORS.length] }));

    // Top nominators
    const nominatorCounts = {};
    noms.forEach((n) => {
      nominatorCounts[n.sender_id] = (nominatorCounts[n.sender_id] || 0) + 1;
    });
    const topNominators = Object.entries(nominatorCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([uid, count]) => ({ user: usersMap[uid], count }));

    // Top nominees
    const topNominees = Object.entries(recipientCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([uid, count]) => ({ user: usersMap[uid], count }));

    return {
      totalNominations: noms.length,
      uniqueNominators: uniqueNominators.size,
      uniqueNominees: uniqueNominees.size,
      mostNominatedUser: topRecipient?.full_name || topRecipient?.email || t("analytics.not_available"),
      perAwardData,
      timelineData,
      pieData,
      topNominators,
      topNominees,
    };
  }, [campaign?.nominations, awards, usersMap, t, fmt]);

  // ── Loading / Error States ──

  if (campaignLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="p-6">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-slate-600 hover:text-purple-600 mb-6">
          <ArrowLeft className="w-5 h-5" />
          {t("campaign.back_to_admin")}
        </button>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <p className="text-red-700">{t("campaign.not_found")}</p>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining(campaign.end_date);
  const isEnded = daysRemaining < 0;
  const isFinalized = !!campaign.winner_awarded_at;
  const selectedWinnersCount = awards.filter((a) => a.winner_status === "approved" && a.is_active).length;
  const activeAwardsCount = awards.filter((a) => a.is_active).length;
  const allWinnersSelected = activeAwardsCount > 0 && selectedWinnersCount === activeAwardsCount;
  const isDraft = campaign.status === "draft";
  const canActivate = isDraft && activeAwardsCount > 0;

  // ── Tab Content Renderers ──

  const renderOverviewTab = () => (
    <>
      {/* Info Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-blue-600" />
            <p className="text-xs text-blue-700 font-medium">{t("overview.duration")}</p>
          </div>
          <p className="text-sm text-blue-900">
            {t("overview.duration_range", { start: fmt.date(campaign.start_date), end: fmt.date(campaign.end_date) })}
          </p>
          {!isEnded && (
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t("overview.days_remaining", { count: daysRemaining })}
            </p>
          )}
        </div>

        <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-purple-600" />
            <p className="text-xs text-purple-700 font-medium">{t("overview.scope")}</p>
          </div>
          <p className="text-sm text-purple-900 capitalize">{campaign.scope === "team" && campaign.target_team_name ? t("overview.scope_team", { name: campaign.target_team_name }) : t(`scope.${campaign.scope}`, { defaultValue: campaign.scope })}</p>
        </div>

        <div className="p-4 bg-green-50 rounded-2xl border border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-green-600" />
            <p className="text-xs text-green-700 font-medium">{t("overview.total_nominations")}</p>
          </div>
          <p className="text-sm text-green-900 font-semibold">{campaign.nominations?.length || 0}</p>
        </div>

        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-amber-700 font-medium">{t("overview.status")}</p>
          </div>
          <p className="text-sm text-amber-900 capitalize">{t(`status.${campaign.status}`, { defaultValue: campaign.status })}</p>
          {isFinalized && <p className="text-xs text-green-600 mt-1">{t("campaign.finalized")}</p>}
          {!isFinalized && campaign.status === "completed" && (
            <p className="text-xs text-amber-600 mt-1">{t("overview.winners_selected_ratio", { selected: selectedWinnersCount, total: activeAwardsCount })}</p>
          )}
        </div>
      </div>

      {/* Awards Section */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-600" />
            {t("overview.awards_title")}
          </h2>
          <button onClick={openForCreate} className="flex items-center gap-2 px-4 py-2 nomination-gradient text-white rounded-full transition-all text-sm">
            <Plus className="w-4 h-4" />
            {t("overview.add_award")}
          </button>
        </div>

        <p className="text-sm text-slate-500 mb-6">
          {t("overview.awards_help")}
        </p>

        {awardsLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : awards.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200">
            <Award className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 mb-4">{t("overview.no_awards")}</p>
            <button onClick={openForCreate} className="inline-flex items-center gap-2 px-4 py-2 nomination-gradient text-white rounded-full transition-all text-sm">
              <Plus className="w-4 h-4" />
              {t("overview.create_first_award")}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {awards.map((award) => {
              const colorFrom = extractHexColor(award.color_from) || "#a855f7";
              const colorTo = extractHexColor(award.color_to) || "#3b82f6";
              const gradientStyle = { background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` };
              const hasWinner = award.winner_status === "approved";

              return (
                <div key={award.id} className="flex items-center justify-between p-4 rounded-2xl border bg-white border-slate-200">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-md" style={gradientStyle}>
                      {isCustomIconUrl(award.emoji) ? <img src={award.emoji} alt="" className="w-7 h-7 object-contain" /> : (award.emoji || "⭐")}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{award.name}</span>
                        {hasWinner && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3" />
                            {t("overview.winner_approved")}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-500">{t("ranking_pts", { count: award.points_value })}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {deleteConfirmId === award.id ? (
                      <>
                        <button onClick={() => deleteAwardMutation.mutate(award.id)} disabled={deleteAwardMutation.isPending} className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors" title={t("overview.confirm_delete")}>
                          <Check className="w-5 h-5" />
                        </button>
                        <button onClick={() => setDeleteConfirmId(null)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-full transition-colors" title={t("common:actions.cancel")}>
                          <X className="w-5 h-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => openForEdit(award)} className="p-2 text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors" title={t("common:actions.edit")}>
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button onClick={() => setDeleteConfirmId(award.id)} className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors" title={t("common:actions.delete")}>
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Nominations count */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{t("overview.nominations_title")}</h2>
        <div className="p-8 bg-slate-50 rounded-2xl border border-slate-200 text-center">
          <p className="text-sm text-slate-600">
            {campaign.nominations?.length > 0
              ? t("overview.nominations_received", { count: campaign.nominations.length })
              : t("overview.no_nominations")}
          </p>
        </div>
      </div>
    </>
  );

  const renderLeaderboardTab = () => {
    const noms = campaign?.nominations || [];
    const uniqueNominators = new Set(noms.map((n) => n.sender_id)).size;
    const uniqueNominees = new Set(noms.map((n) => n.recipient_id)).size;
    const engagementRate = noms.length > 0 && uniqueNominators > 0 ? ((uniqueNominees / uniqueNominators) * 100).toFixed(1) : "0.0";

    return (
      <>
        {/* KPI Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile icon={BarChart3} label={t("leaderboard.total_nominations")} value={fmt.number(noms.length)} color="purple" />
          <KpiTile icon={Users} label={t("leaderboard.unique_nominees")} value={fmt.number(uniqueNominees)} color="blue" />
          <KpiTile icon={TrendingUp} label={t("leaderboard.unique_nominators")} value={fmt.number(uniqueNominators)} color="green" />
          <KpiTile icon={Target} label={t("leaderboard.engagement_rate")} value={`${engagementRate}%`} color="yellow" />
        </div>

        {/* Per-award leaderboard tables */}
        {leaderboard.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">{t("leaderboard.no_data")}</p>
          </div>
        ) : (
          leaderboard.map((entry) => {
            const aw = entry.award;
            const rankings = [...entry.rankings].sort((a, b) => b.nomination_count - a.nomination_count);
            const colorFrom = extractHexColor(aw.color_from) || "#a855f7";
            const colorTo = extractHexColor(aw.color_to) || "#3b82f6";

            return (
              <div key={aw.id} className="bg-white rounded-2xl shadow-md overflow-hidden">
                <div className="p-4 flex items-center gap-3" style={{ background: `linear-gradient(to right, ${colorFrom}20, ${colorTo}20)` }}>
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-sm flex-shrink-0" style={{ background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}>
                    {isCustomIconUrl(aw.emoji) ? <img src={aw.emoji} alt="" className="w-6 h-6 object-contain" /> : (aw.emoji || "⭐")}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{aw.name}</h3>
                    <p className="text-xs text-slate-500">{t("leaderboard.award_summary", { nominations: fmt.number(entry.total_nominations), points: fmt.number(aw.points_value) })}</p>
                  </div>
                </div>

                {rankings.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">{t("leaderboard.no_award_nominations")}</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 w-16">{t("leaderboard.col_rank")}</th>
                        <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("leaderboard.col_user")}</th>
                        <th className="text-right text-xs font-medium text-slate-500 px-4 py-3">{t("leaderboard.col_nominations")}</th>
                        <th className="text-right text-xs font-medium text-slate-500 px-4 py-3">{t("leaderboard.col_points")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankings.map((r, idx) => {
                        const user = r.user || usersMap[r.user_id];
                        const nominationsUrl = `/admin/campaigns/${id}/nominations?award_id=${aw.id}&recipient_id=${r.user_id}`;
                        return (
                          <tr
                            key={r.user_id}
                            className={`border-b border-slate-50 cursor-pointer hover:bg-purple-50/50 transition-colors ${idx === 0 ? "bg-amber-50/50 hover:bg-amber-50" : ""}`}
                            onClick={() => navigate(nominationsUrl)}
                          >
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-1">
                                <span className="font-medium">{r.rank}</span>
                                {r.is_tied && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">{t("tie")}</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <UserAvatar src={user?.avatar_url} name={user?.full_name || user?.email} className="w-7 h-7 rounded-full object-cover" textClassName="text-xs" />
                                <span className="text-sm text-slate-900 truncate">{user?.full_name || user?.email || t("unknown_user")}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-purple-600 underline">{fmt.number(r.nomination_count)}</td>
                            <td className="px-4 py-3 text-right text-sm text-slate-500">{fmt.number(r.total_points)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })
        )}
      </>
    );
  };

  const renderWinnersTab = () => {
    if (campaign.status !== "completed") {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="text-amber-800 font-medium">{t("winners.must_end_title")}</p>
          <p className="text-sm text-amber-600 mt-1">{t("winners.must_end_body")}</p>
        </div>
      );
    }

    return (
      <>
        {/* Progress */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              {t("winners.progress_title")}
            </h2>
            {isFinalized && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                {t("campaign.finalized")}
              </span>
            )}
          </div>

          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">{t("winners.progress", { selected: selectedWinnersCount, total: activeAwardsCount })}</span>
              <span className="font-medium text-slate-900">{activeAwardsCount > 0 ? Math.round((selectedWinnersCount / activeAwardsCount) * 100) : 0}%</span>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full nomination-gradient rounded-full transition-all duration-300"
                style={{ width: `${activeAwardsCount > 0 ? (selectedWinnersCount / activeAwardsCount) * 100 : 0}%` }}
              />
            </div>
          </div>

          {!isFinalized && (
            <button
              onClick={handleFinalize}
              disabled={!allWinnersSelected || finalizeMutation.isPending}
              className="w-full px-4 py-3 nomination-gradient text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {finalizeMutation.isPending ? t("winners.finalizing") : allWinnersSelected ? t("winners.finalize") : t("winners.select_all_to_finalize", { count: activeAwardsCount })}
            </button>
          )}
        </div>

        {/* Award cards with winner selection */}
        {awards.filter((a) => a.is_active).map((award) => {
          const colorFrom = extractHexColor(award.color_from) || "#a855f7";
          const colorTo = extractHexColor(award.color_to) || "#3b82f6";
          const hasWinner = award.winner_status === "approved";
          const winnerUser = hasWinner ? usersMap[award.winner_user_id] : null;
          const awardLeaderboard = leaderboard.find((e) => e.award.id === award.id);
          const top3 = (awardLeaderboard?.rankings || []).slice(0, 3);

          return (
            <div key={award.id} className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="p-4 flex items-center gap-3" style={{ background: `linear-gradient(to right, ${colorFrom}20, ${colorTo}20)` }}>
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl shadow-sm" style={{ background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}>
                  {isCustomIconUrl(award.emoji) ? <img src={award.emoji} alt="" className="w-6 h-6 object-contain" /> : (award.emoji || "⭐")}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{award.name}</h3>
                  <p className="text-xs text-slate-500">{t("ranking_pts", { count: award.points_value })}</p>
                </div>
                {hasWinner && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <CheckCircle2 className="w-3 h-3" />
                    {t("winners.winner_selected")}
                  </span>
                )}
              </div>

              <div className="p-4">
                {hasWinner ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-2xl border border-green-200">
                    <div className="flex items-center gap-3">
                      <Crown className="w-5 h-5 text-amber-500" />
                      <div className="flex items-center gap-2">
                        <UserAvatar src={winnerUser?.avatar_url} name={winnerUser?.full_name || winnerUser?.email} className="w-8 h-8 rounded-full object-cover" textClassName="text-xs" />
                        <div>
                          <p className="font-medium text-slate-900">{winnerUser?.full_name || winnerUser?.email || award.winner_user_id}</p>
                          {award.admin_note && <p className="text-xs text-slate-500 mt-0.5">{award.admin_note}</p>}
                        </div>
                      </div>
                    </div>
                    {!isFinalized && (
                      <button
                        onClick={() => clearWinnerMutation.mutate(award.id)}
                        disabled={clearWinnerMutation.isPending}
                        className="px-3 py-1.5 text-sm border border-slate-300 text-slate-600 rounded-full hover:bg-slate-50 transition-colors"
                      >
                        {t("winners.change")}
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Top 3 nominees */}
                    {top3.length > 0 ? (
                      <div className="space-y-2 mb-4">
                        <p className="text-xs font-medium text-slate-500">{t("winners.top_nominees")}</p>
                        {top3.map((r) => {
                          const user = r.user || usersMap[r.user_id];
                          return (
                            <div key={r.user_id} className="flex items-center justify-between p-2 rounded-2xl hover:bg-slate-50">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-600 w-6">{t("rank", { rank: r.rank })}</span>
                                <UserAvatar src={user?.avatar_url} name={user?.full_name || user?.email} className="w-6 h-6 rounded-full object-cover" textClassName="text-[10px]" />
                                <span className="text-sm text-slate-900 truncate">{user?.full_name || user?.email || t("unknown_user")}</span>
                                {r.is_tied && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">{t("tie")}</span>}
                              </div>
                              <span className="text-xs text-slate-500">{t("winners.noms", { count: r.nomination_count })}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 mb-4">{t("leaderboard.no_award_nominations")}</p>
                    )}
                    <button
                      onClick={() => { setShowWinnerModal(award); setWinnerAdminNote(""); }}
                      className="w-full px-4 py-2 nomination-bg nomination-btn text-white rounded-full transition-all text-sm font-medium"
                    >
                      {t("winners.select_winner")}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderAnalyticsTab = () => {
    if (!analyticsData) {
      return (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <PieChart className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">{t("analytics.no_data")}</p>
        </div>
      );
    }

    return (
      <>
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile icon={BarChart3} label={t("analytics.total_nominations")} value={fmt.number(analyticsData.totalNominations)} color="purple" />
          <KpiTile icon={Users} label={t("analytics.unique_nominators")} value={fmt.number(analyticsData.uniqueNominators)} color="blue" />
          <KpiTile icon={Crown} label={t("analytics.most_nominated")} value={analyticsData.mostNominatedUser} color="yellow" />
          <KpiTile icon={Award} label={t("analytics.award_categories")} value={fmt.number(analyticsData.perAwardData.length)} color="green" />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Nominations per Award */}
          {analyticsData.perAwardData.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("analytics.per_award")}</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analyticsData.perAwardData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Award Popularity Pie */}
          {analyticsData.pieData.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("analytics.popularity")}</h3>
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPieChart>
                  <Pie data={analyticsData.pieData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {analyticsData.pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Timeline */}
        {analyticsData.timelineData.length > 1 && (
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("analytics.timeline")}</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={analyticsData.timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#8b5cf6" fill="#8b5cf620" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Active Nominators */}
          {analyticsData.topNominators.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("analytics.top_nominators")}</h3>
              <div className="space-y-2">
                {analyticsData.topNominators.map((entry, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-400 w-5">{idx + 1}</span>
                      <span className="text-sm text-slate-900">{entry.user?.full_name || entry.user?.email || t("unknown")}</span>
                    </div>
                    <span className="text-sm font-medium text-purple-600">{fmt.number(entry.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Most Nominated Users */}
          {analyticsData.topNominees.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("analytics.top_nominees")}</h3>
              <div className="space-y-2">
                {analyticsData.topNominees.map((entry, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-400 w-5">{idx + 1}</span>
                      <span className="text-sm text-slate-900">{entry.user?.full_name || entry.user?.email || t("unknown")}</span>
                    </div>
                    <span className="text-sm font-medium text-purple-600">{fmt.number(entry.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case "overview": return renderOverviewTab();
      case "leaderboard": return renderLeaderboardTab();
      case "winners": return renderWinnersTab();
      case "analytics": return renderAnalyticsTab();
      default: return renderOverviewTab();
    }
  };

  // ── Render ──

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-slate-600 hover:text-purple-600 mb-2">
            <ArrowLeft className="w-5 h-5" />
            {t("campaign.back_to_admin")}
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-800">{campaign.name}</h1>
            {getStatusBadge(campaign.status, t)}
            {isFinalized && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3" />
                {t("campaign.finalized")}
              </span>
            )}
          </div>
          {campaign.description && <p className="text-sm text-slate-600 mt-1">{campaign.description}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
          <button onClick={openEditCampaignModal} className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">
            <Edit2 className="w-4 h-4" />
            {t("common:actions.edit")}
          </button>
          {campaign.is_active && campaign.status === "active" ? (
            <button onClick={() => pauseMutation.mutate()} disabled={pauseMutation.isPending} className="flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-700 rounded-full hover:bg-amber-50 transition-colors disabled:opacity-50">
              <Pause className="w-4 h-4" />
              {t("campaign.pause")}
            </button>
          ) : campaign.status === "paused" || isDraft ? (
            <button
              onClick={() => canActivate && toggleStatusMutation.mutate(true)}
              disabled={!canActivate || toggleStatusMutation.isPending}
              title={!canActivate ? t("campaign.activate_disabled_title") : t("campaign.activate_title")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors disabled:opacity-50 ${
                canActivate
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              <Play className="w-4 h-4" />
              {t("campaign.activate")}
            </button>
          ) : null}
          {(campaign.status === "active" || campaign.status === "paused") && !isFinalized && (
            <button onClick={handleEndCampaign} disabled={endCampaignMutation.isPending} className="flex items-center gap-2 px-4 py-2 border border-orange-300 text-orange-700 rounded-full hover:bg-orange-50 transition-colors disabled:opacity-50">
              <StopCircle className="w-4 h-4" />
              {t("campaign.end_campaign")}
            </button>
          )}
          <button onClick={handleDeleteCampaign} disabled={deleteCampaignMutation.isPending} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50">
            <Trash2 className="w-4 h-4" />
            {t("common:actions.delete")}
          </button>
          </div>
          {isDraft && !canActivate && !awardsLoading && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t("campaign.activate_hint")}
            </p>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 p-2">
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all duration-200 ${
                  isActive
                    ? "nomination-gradient text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="hidden sm:inline">{t(`tabs.${tab.id}`)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <motion.div key={currentTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
        {renderTabContent()}
      </motion.div>

      {/* ── Modals ── */}

      {/* Award Create/Edit Modal */}
      <AnimatePresence>
        {showAwardModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={closeAwardModal} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">{editingAward ? t("award_form.edit_title") : t("award_form.new_title")}</h3>
                <button type="button" onClick={closeAwardModal} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("award_form.name")} <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t("award_form.name_placeholder")} className="w-full px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent" maxLength={100} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("award_form.ranking_points")}</label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setFormData({ ...formData, points_value: Math.max(0, formData.points_value - 10) })} disabled={formData.points_value <= 0} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
                      <span className="text-lg font-bold">−</span>
                    </button>
                    <input type="number" min="0" value={formData.points_value} onChange={(e) => setFormData({ ...formData, points_value: Math.max(0, parseInt(e.target.value) || 0) })} className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                    <button type="button" onClick={() => setFormData({ ...formData, points_value: formData.points_value + 10 })} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
                      <span className="text-lg font-bold">+</span>
                    </button>
                    <span className="text-sm text-slate-500">{t("award_form.pts")}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t("award_form.points_help")}</p>
                </div>

                {/* Icon Picker */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">{t("award_form.icon")}</label>

                  {/* Tab Buttons */}
                  <div className="flex gap-2 mb-3">
                    <button type="button" onClick={() => setIconTab("emojis")} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${iconTab === "emojis" ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      <Smile className="w-4 h-4" />
                      {t("award_form.tab_icons")}
                    </button>
                    <button type="button" onClick={() => { setIconTab("custom"); refetchCustomIcons(); }} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${iconTab === "custom" ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      <Image className="w-4 h-4" />
                      {t("award_form.tab_custom_icons")}
                    </button>
                  </div>

                  {/* Emojis Tab */}
                  {iconTab === "emojis" && (
                    <div>
                      <div className="grid grid-cols-8 gap-2 mb-2">
                        {QUICK_EMOJIS.map((emoji) => (
                          <button key={emoji} type="button" onClick={() => setFormData({ ...formData, emoji })} className={`p-2 text-2xl rounded-2xl transition-all ${formData.emoji === emoji ? "bg-purple-100 ring-2 ring-purple-500" : "hover:bg-slate-100"}`}>
                            {emoji}
                          </button>
                        ))}
                      </div>
                      <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 mb-2">
                        <Smile className="w-4 h-4" />
                        {showEmojiPicker ? t("common:actions.show_less") : t("award_form.more_emojis")}
                      </button>
                      {showEmojiPicker && (
                        <div className="border border-slate-200 rounded-2xl p-3 mt-2 max-h-60 overflow-y-auto">
                          {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                            <div key={category} className="mb-3">
                              <div className="text-xs font-medium text-slate-500 mb-1">{t(`award_form.emoji_categories.${category}`)}</div>
                              <div className="grid grid-cols-8 gap-1">
                                {emojis.map((emoji) => (
                                  <button key={`${category}-${emoji}`} type="button" onClick={() => setFormData({ ...formData, emoji })} className={`p-1.5 text-xl rounded transition-all ${formData.emoji === emoji ? "bg-purple-100 ring-2 ring-purple-500" : "hover:bg-slate-100"}`}>
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-2">
                        <input type="text" value={isCustomIconUrl(formData.emoji) ? "" : formData.emoji} onChange={(e) => setFormData({ ...formData, emoji: e.target.value.slice(0, 2) })} placeholder={t("award_form.emoji_placeholder")} className="w-full px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-2xl" maxLength={2} />
                      </div>
                    </div>
                  )}

                  {/* Custom Icons Tab */}
                  {iconTab === "custom" && (
                    <div>
                      <label className="flex items-center justify-center gap-2 w-full p-3 mb-3 border-2 border-dashed border-slate-300 rounded-2xl text-slate-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors cursor-pointer">
                        <Upload className="w-5 h-5" />
                        <span className="text-sm font-medium">{uploadIconMutation.isPending ? t("award_form.uploading") : t("award_form.upload_custom_icon")}</span>
                        <input type="file" accept=".png,.ico" onChange={handleIconUpload} disabled={uploadIconMutation.isPending} className="hidden" />
                      </label>
                      <p className="text-xs text-slate-500 mb-3">{t("award_form.upload_help")}</p>
                      {customIcons.length > 0 ? (
                        <div className="grid grid-cols-6 gap-2">
                          {customIcons.map((icon) => (
                            <div key={icon.id} className="relative group">
                              <button type="button" onClick={() => setFormData({ ...formData, emoji: icon.url })} className={`w-full aspect-square rounded-xl border-2 p-1.5 flex items-center justify-center transition-all ${formData.emoji === icon.url ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`} title={icon.filename}>
                                <img src={icon.url} alt={icon.filename} className="w-8 h-8 object-contain" />
                              </button>
                              <button type="button" onClick={(e) => { e.stopPropagation(); if (formData.emoji === icon.url) setFormData({ ...formData, emoji: "⭐" }); deleteIconMutation.mutate(icon.id); }} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs" title={t("award_form.delete_icon")}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-slate-400 text-sm">{t("award_form.no_custom_icons")}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Color Picker */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">{t("award_form.gradient_color")}</label>
                  <div className="mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <button type="button" onClick={() => setPickingColor(pickingColor === "from" ? null : "from")} className={`flex-1 p-3 rounded-2xl border-2 transition-all ${pickingColor === "from" ? "border-purple-500 ring-2 ring-purple-200" : "border-slate-200 hover:border-slate-300"}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-2xl shadow-inner border border-slate-200" style={{ backgroundColor: formData.color_from }} />
                          <div className="text-left"><div className="text-xs text-slate-500">{t("award_form.start")}</div><div className="text-sm font-medium text-slate-700">{pickingColor === "from" ? t("award_form.pick_below") : t("award_form.click_to_change")}</div></div>
                        </div>
                      </button>
                      <div className="text-slate-400">→</div>
                      <button type="button" onClick={() => setPickingColor(pickingColor === "to" ? null : "to")} className={`flex-1 p-3 rounded-2xl border-2 transition-all ${pickingColor === "to" ? "border-purple-500 ring-2 ring-purple-200" : "border-slate-200 hover:border-slate-300"}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-2xl shadow-inner border border-slate-200" style={{ backgroundColor: formData.color_to }} />
                          <div className="text-left"><div className="text-xs text-slate-500">{t("award_form.end")}</div><div className="text-sm font-medium text-slate-700">{pickingColor === "to" ? t("award_form.pick_below") : t("award_form.click_to_change")}</div></div>
                        </div>
                      </button>
                    </div>
                    <div className="h-10 rounded-2xl shadow-inner border border-slate-200" style={{ background: `linear-gradient(to right, ${formData.color_from}, ${formData.color_to})` }} />
                  </div>
                  {pickingColor && (
                    <div className="mb-4 p-3 border border-purple-200 rounded-2xl bg-purple-50/50">
                      <div className="text-xs font-medium text-purple-700 mb-2">{pickingColor === "from" ? t("award_form.select_start_color") : t("award_form.select_end_color")}</div>
                      {/* Eyedropper + Hex input */}
                      <div className="flex items-center gap-2 mb-3">
                        <label className="relative w-10 h-10 flex-shrink-0 cursor-pointer" title={t("award_form.eyedropper_title")}>
                          <div
                            className="w-10 h-10 rounded-full border-2 border-slate-200 shadow-inner"
                            style={{ backgroundColor: pickingColor === "from" ? formData.color_from : formData.color_to }}
                          />
                          <input
                            type="color"
                            value={pickingColor === "from" ? formData.color_from : formData.color_to}
                            onChange={(e) => {
                              const c = e.target.value;
                              if (pickingColor === "from") setFormData({ ...formData, color_from: c });
                              else setFormData({ ...formData, color_to: c });
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                        </label>
                        <input
                          type="text"
                          key={`${pickingColor}-${pickingColor === "from" ? formData.color_from : formData.color_to}`}
                          defaultValue={pickingColor === "from" ? formData.color_from : formData.color_to}
                          onChange={(e) => {
                            let val = e.target.value.trim();
                            if (!val.startsWith("#")) val = "#" + val;
                            if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                              if (pickingColor === "from") setFormData({ ...formData, color_from: val });
                              else setFormData({ ...formData, color_to: val });
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                          placeholder="#000000"
                          maxLength={7}
                        />
                      </div>
                      <div className="space-y-1">
                        {COLOR_PALETTE.map((row, rowIndex) => (
                          <div key={rowIndex} className="flex gap-1">
                            {row.map((color) => (
                              <button key={color} type="button" onClick={() => handleColorSelect(color)} className={`flex-1 h-7 rounded transition-all hover:scale-110 hover:z-10 ${((pickingColor === "from" && formData.color_from === color) || (pickingColor === "to" && formData.color_to === color)) ? "ring-2 ring-purple-500 ring-offset-1" : ""}`} style={{ backgroundColor: color }} title={color} />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-medium text-slate-500 mb-2">{t("award_form.quick_presets")}</div>
                    <div className="grid grid-cols-4 gap-2">
                      {GRADIENT_PRESETS.map((preset, index) => (
                        <button key={index} type="button" onClick={() => handleGradientPreset(preset)} className={`h-10 rounded-2xl transition-all ${formData.color_from === preset.from && formData.color_to === preset.to ? "ring-2 ring-offset-2 ring-purple-500" : "hover:scale-105"}`} style={{ background: `linear-gradient(to right, ${preset.from}, ${preset.to})` }} title={t(`award_form.gradient_presets.${preset.key}`)} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded" />
                  <label htmlFor="is_active" className="ml-2 text-sm text-slate-700">{t("award_form.is_active")}</label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("award_form.preview")}</label>
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-md" style={{ background: `linear-gradient(to right, ${formData.color_from}, ${formData.color_to})` }}>
                      {isCustomIconUrl(formData.emoji) ? <img src={formData.emoji} alt="" className="w-7 h-7 object-contain" /> : formData.emoji}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{formData.name || t("award_form.preview_name")}</div>
                      <div className="text-sm text-slate-500">{t("ranking_pts", { count: formData.points_value })}</div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={closeAwardModal} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">{t("common:actions.cancel")}</button>
                  <button type="submit" disabled={createAwardMutation.isPending || updateAwardMutation.isPending} className="flex-1 px-4 py-2 nomination-bg nomination-btn text-white rounded-full transition-all disabled:opacity-50">
                    {createAwardMutation.isPending || updateAwardMutation.isPending ? t("common:actions.saving") : editingAward ? t("common:actions.update") : t("common:actions.create")}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Campaign Modal */}
      <AnimatePresence>
        {showEditCampaignModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowEditCampaignModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg">
              <div className="p-6 border-b border-slate-200"><h3 className="text-lg font-semibold text-slate-900">{t("edit_campaign.title")}</h3></div>
              <form onSubmit={handleCampaignSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("edit_campaign.name")} <span className="text-red-500">*</span></label>
                  <input type="text" value={campaignFormData.name} onChange={(e) => setCampaignFormData({ ...campaignFormData, name: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent" maxLength={255} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("edit_campaign.description")}</label>
                  <textarea value={campaignFormData.description} onChange={(e) => setCampaignFormData({ ...campaignFormData, description: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent" rows={3} maxLength={1000} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("edit_campaign.start_date")}</label>
                    <input type="date" value={campaignFormData.start_date} onChange={(e) => setCampaignFormData({ ...campaignFormData, start_date: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t("edit_campaign.end_date")}</label>
                    <input type="date" value={campaignFormData.end_date} onChange={(e) => setCampaignFormData({ ...campaignFormData, end_date: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowEditCampaignModal(false)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">{t("common:actions.cancel")}</button>
                  <button type="submit" disabled={updateCampaignMutation.isPending} className="flex-1 px-4 py-2 nomination-bg nomination-btn text-white rounded-full transition-all disabled:opacity-50">
                    {updateCampaignMutation.isPending ? t("common:actions.saving") : t("common:actions.save_changes")}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Winner Selection Modal */}
      <AnimatePresence>
        {showWinnerModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowWinnerModal(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">{t("winner_modal.title", { name: showWinnerModal.name })}</h3>
                <p className="text-sm text-slate-500 mt-1">{t("winner_modal.points_note", { points: fmt.number(showWinnerModal.points_value) })}</p>
              </div>

              <div className="p-6 space-y-4">
                {(() => {
                  const awardLb = leaderboard.find((e) => e.award.id === showWinnerModal.id);
                  const rankings = awardLb?.rankings || [];

                  if (rankings.length === 0) {
                    return <p className="text-sm text-slate-500 text-center py-4">{t("winner_modal.no_nominees")}</p>;
                  }

                  return (
                    <div className="space-y-2">
                      {rankings.map((r) => {
                        const user = r.user || usersMap[r.user_id];
                        return (
                          <button
                            key={r.user_id}
                            type="button"
                            onClick={() => {
                              selectWinnerMutation.mutate({
                                awardId: showWinnerModal.id,
                                userId: r.user_id,
                                adminNote: winnerAdminNote || null,
                              });
                            }}
                            disabled={selectWinnerMutation.isPending}
                            className="w-full flex items-center justify-between p-3 rounded-2xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all text-left"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-slate-500 w-6">{t("rank", { rank: r.rank })}</span>
                              <UserAvatar src={user?.avatar_url} name={user?.full_name || user?.email} className="w-8 h-8 rounded-full object-cover" textClassName="text-xs" />
                              <div>
                                <p className="font-medium text-slate-900">{user?.full_name || user?.email || r.user_id}</p>
                                <p className="text-xs text-slate-500">{t("winner_modal.nominee_summary", { count: r.nomination_count, points: fmt.number(r.total_points) })}</p>
                              </div>
                              {r.is_tied && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">{t("tie")}</span>}
                            </div>
                            <Crown className="w-5 h-5 text-slate-300" />
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t("winner_modal.admin_note")}</label>
                  <textarea
                    value={winnerAdminNote}
                    onChange={(e) => setWinnerAdminNote(e.target.value)}
                    placeholder={t("winner_modal.admin_note_placeholder")}
                    className="w-full px-4 py-2 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={2}
                    maxLength={500}
                  />
                </div>

                <button type="button" onClick={() => setShowWinnerModal(null)} className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">
                  {t("common:actions.cancel")}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CampaignPage;
